import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { QueryClient } from '@tanstack/react-query';
import { GraphQLError } from 'graphql';
import { GraphQLClient } from 'graphql-request';
import { nanoid } from 'nanoid';

import { EnvironmentType } from '../../../environment/types/environment.types.ts';

import { AbortRequestEnum } from '../../constants/abort-request.ts';
import { ErrorTypeEnum } from '../../constants/error-type.ts';
import { mapUnauthorizedMessage } from '../../mappers/unauthorized-message.mapper.ts';

import { getAwsAuthUtil } from '../aws/aws-auth.util.ts';
import { getHashUtil } from '../hash.util.ts';
import { getSentryUtil } from '../monitoring/sentry.util.ts';

export class ReadingCrittersClientUtil {
  private static _instance: ReadingCrittersClientUtil;
  private client?: QueryClient;
  private graphQLClient?: GraphQLClient;
  private readonly CLIENT_NAME = 'ReadingCrittersClientUtil';
  private abortControllers: Record<string, AbortController> = {};
  private readonly API_KEY_HEADER = 'x-api-key';
  private readonly AUTHORIZATION_HEADER = 'Authorization';

  private constructor(
    private readonly authUtil = getAwsAuthUtil(),
    private readonly sentryUtil = getSentryUtil(),
    private readonly hashUtil = getHashUtil()
  ) {}

  public static get instance(): ReadingCrittersClientUtil {
    if (!ReadingCrittersClientUtil._instance) {
      ReadingCrittersClientUtil._instance = new ReadingCrittersClientUtil();
    }
    return ReadingCrittersClientUtil._instance;
  }

  public abortAllRequests(reason: AbortRequestEnum): void {
    Object.keys(this.abortControllers).forEach((key) => {
      this.abortControllers[key].abort(reason);
      delete this.abortControllers[key];
    });
  }

  public initialize(env: EnvironmentType): void {
    this.client = new QueryClient();

    this.graphQLClient = new GraphQLClient(env.apiUrl, { headers: { [this.API_KEY_HEADER]: env.apiKey } });
  }

  private async getAuthHeaders(useAuth: boolean): Promise<HeadersInit | undefined> {
    if (useAuth) {
      const idToken = await this.authUtil.getIdToken();
      return { [this.AUTHORIZATION_HEADER]: idToken || '', [this.API_KEY_HEADER]: '' };
    }
    return undefined;
  }

  private async getScopeKeys(keys: string[], useAuth: boolean): Promise<string[]> {
    if (useAuth) {
      const idToken = await this.authUtil.getIdToken();
      if (idToken) {
        return [idToken, ...keys];
      }
    }
    return keys;
  }

  public async query<R, V extends object | undefined>(
    query: TypedDocumentNode<R, V>,
    variables: V,
    keys: string[],
    { bypassCache = false, useAuth = false }: { bypassCache?: boolean; useAuth?: boolean } = {}
  ): Promise<R> {
    if (!this.client) {
      throw new Error(ErrorTypeEnum.CLIENT_NOT_INITIALIZED);
    }

    const variablesHash = this.hashUtil.hash(variables);
    const scopedKeys = await this.getScopeKeys(keys.concat(variablesHash), useAuth);

    if (bypassCache) {
      this.invalidate(scopedKeys);
    }

    const result = await this.client.fetchQuery({
      queryKey: scopedKeys,
      queryFn: async () => this.request(query, variables, { useAuth, keys: scopedKeys }),
    });

    return result;
  }

  public async mutation<R, V extends object | undefined>(
    document: TypedDocumentNode<R, V>,
    variables: V,
    options: { useAuth: boolean }
  ): Promise<R> {
    return this.request(document, variables, options);
  }

  private async request<R, V extends object | undefined>(
    document: TypedDocumentNode<R, V>,
    variables: V,
    options: { useAuth: boolean; keys?: string[] }
  ): Promise<R> {
    if (!this.graphQLClient) {
      throw new Error(ErrorTypeEnum.CLIENT_NOT_INITIALIZED);
    }

    const { useAuth, keys = [] } = options;
    const uniqueRequestId = nanoid(12);
    this.abortControllers[uniqueRequestId] = new AbortController();
    try {
      return await this.graphQLClient.request({
        document,
        variables,
        requestHeaders: await this.getAuthHeaders(useAuth),
        signal: this.abortControllers[uniqueRequestId].signal,
      });
    } catch (error: unknown) {
      // TODO: Improve error handling
      if ((error as { response: { errors: GraphQLError[] } }).response?.errors) {
        const errors = (error as { response: { errors: GraphQLError[] } }).response.errors.map(
          (graphQLError: GraphQLError) => {
            if (mapUnauthorizedMessage(graphQLError.message)) {
              this.authUtil.forceRefreshToken();
            }

            const enhancedError = new Error(graphQLError.message);
            enhancedError.name = `${ErrorTypeEnum.GRAPHQL}`;

            this.sentryUtil.trackError(
              enhancedError,
              {
                client: this.CLIENT_NAME,
                errorType: ErrorTypeEnum.GRAPHQL,
              },
              {
                paths: graphQLError.path,
                locations: graphQLError.locations,
              }
            );
            return enhancedError;
          }
        );
        if (errors.length > 0) {
          throw errors[0];
        }
      }

      // Handle network errors
      const networkError = new Error((error as { message: string }).message);
      networkError.name = `${ErrorTypeEnum.GRAPHQL_NETWORK}: ${keys.join('.')}`;
      networkError.stack = (error as { stack: string }).stack;

      this.sentryUtil.trackError(networkError, {
        client: this.CLIENT_NAME,
        errorType: ErrorTypeEnum.GRAPHQL_NETWORK,
        networkErrorName: (error as { name: string }).name,
      });

      throw error;
    } finally {
      delete this.abortControllers[uniqueRequestId];
    }
  }

  public invalidate(keys: string[]): void {
    this.client?.invalidateQueries({ queryKey: keys });
  }

  public async invalidateCacheForCurrentUser(): Promise<void> {
    const currentUser = await this.authUtil.getCurrentUser();
    if (currentUser) {
      this.invalidate([currentUser.userId]);
    }
  }
}

export const getReadingCrittersClientUtil = (): ReadingCrittersClientUtil => ReadingCrittersClientUtil.instance;
